import { initialize, LDClient, LDFlagSet } from 'launchdarkly-js-client-sdk';
import { ComponentType, createContext, ReactNode, useContext, useEffect, useState } from 'react';
import { SubscriptionPlanPrice } from '../models/subscriptionPlanPrice';
import { DirectEcomTransition } from '../models/directEcomTransition';
import { useQuery } from '../routes';
import { TruePlatformConfig } from '../models';

export interface LaunchDarklyProps {
  client?: LDClient;
  initialized?: boolean;
  flags?: LaunchDarklyFlag;
  currentOrganizationId?: number;
  currentAccountId?: number;
  changeAccount?: (accountId: number) => Promise<void>;
}

export interface LaunchDarklyFlag extends Omit<LDFlagSet, keyof LDFlagSet> {
  subscriptionAndPaymentMethodDisabled?: boolean;
  unlimitedVTPEnabled?: boolean;
  bankTransferEnabled?: boolean;
  monthlySubsYearEndCampaignConfig?: any;
  truetestUIEnabled?: boolean;
  subscriptionPlanPrice: SubscriptionPlanPrice;
  directEcomTransition?: DirectEcomTransition;
  subscriptionRetryPayment?: boolean;
  paidDomainsPromoExclusionConfig?: string[];
  navigationBarEnabled?: boolean;
  sidebarEnabled?: boolean;
  showPLanBanner?: boolean;
  showSaveCardAndBillingInfoBtn?: boolean;
  orderHistoryActionBtnEnabled?: boolean;
  cancelCreditCardOrderEnabled?: boolean;
  turnOffAutoRenewSurveyEnabled?: boolean;
  cookieExpirationDays?: number;
  trueUpgradeEnabled?: boolean;
  truePlatformEnabled?: boolean;
  feTruePlatformAndTrueAutomationEnabled?: boolean;
  feTrueBundleMoreTestingPlansEnabled?: boolean;
  truePlatformConfig?: TruePlatformConfig;
  isDLT?: boolean;
}

const defaultValue: LaunchDarklyFlag = {
  subscriptionAndPaymentMethodDisabled: false,
  unlimitedVTPEnabled: false,
  bankTransferEnabled: false,
  monthlySubsYearEndCampaignConfig: undefined,
  truetestUIEnabled: false,
  subscriptionPlanPrice: {},
  directEcomTransition: undefined,
  subscriptionRetryPayment: false,
  paidDomainsPromoExclusionConfig: [],
  navigationBarEnabled: false,
  sidebarEnabled: true,
  showPLanBanner: false,
  showSaveCardAndBillingInfoBtn: true,
  orderHistoryActionBtnEnabled: false,
  cancelCreditCardOrderEnabled: false,
  turnOffAutoRenewSurveyEnabled: false,
  cookieExpirationDays: 30,
  trueUpgradeEnabled: false,
  truePlatformEnabled: false,
  feTruePlatformAndTrueAutomationEnabled: false,
  feTrueBundleMoreTestingPlansEnabled: false,
  isDLT: false,
};

const applyLocalFlagOverrides = (flags: LaunchDarklyFlag): LaunchDarklyFlag => {
  if (process.env.NODE_ENV !== 'development') {
    return flags;
  }

  return {
    ...flags,
    ...(process.env.REACT_APP_FE_TRUE_PLATFORM_AND_TRUE_AUTOMATION_ENABLED === 'true'
      ? { feTruePlatformAndTrueAutomationEnabled: true }
      : {}),
    ...(process.env.REACT_APP_FE_TRUE_BUNDLE_MORE_TESTING_PLANS_ENABLED === 'true'
      ? { feTrueBundleMoreTestingPlansEnabled: true }
      : {}),
  };
};

export const LaunchDarklyContext = createContext<LaunchDarklyProps>({});

export const LaunchDarklyProvider = ({ children }: { children: ReactNode }) => {
  const [flags, setFlags] = useState<LaunchDarklyProps['flags']>(
    applyLocalFlagOverrides(defaultValue),
  );
  const [client, setClient] = useState<LaunchDarklyProps['client']>();
  const [initialized, setInitialized] = useState<LaunchDarklyProps['initialized']>(false);
  const { get } = useQuery();
  const accountId = get('accountId');
  const [currentAccountId, setCurrentAccountId] = useState<LaunchDarklyProps['currentAccountId']>();
  const initializeClient = async () => {
    if (!process.env.REACT_APP_LD_KEY || !process.env.REACT_APP_LD_USER) {
      setInitialized(true);
      return Promise.resolve('Missing LaunchDarkly Configuration');
    }
    const ldUser = {
      key: process.env.REACT_APP_LD_USER,
    };
    const ldClient = initialize(process.env.REACT_APP_LD_KEY!!, ldUser);
    setClient(ldClient);
    return ldClient.waitForInitialization()
      .then(() => {
        setInitialized(true);
      });
  };

  const changeAccount = async (accountId: number) => {
    if (!initialized || !client || currentAccountId === accountId) {
      return;
    }
    setCurrentAccountId(accountId);
    const key = `account-${process.env.REACT_APP_LD_PREFIX}-${accountId}`;
    const accountFlags = await client.identify({
      kind: 'user',
      key,
      accountId,
    }) as LaunchDarklyFlag;
    setFlags(applyLocalFlagOverrides(accountFlags));
  };

  const getAllFlags = () => {
    if (initialized && client) {
      const data = client.allFlags();
      setFlags(applyLocalFlagOverrides(data as LaunchDarklyFlag));
      return data;
    }
    return {};
  };

  useEffect(() => {
    if (Number.isNaN(accountId)) {
      getAllFlags();
      return;
    }
    changeAccount(Number(accountId))
      .then(() => {
        // do nothing
      });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [initialized]);

  useEffect(() => {
    initializeClient();
    return function cleanup() {
      if (client) {
        client.close()
          .then(() => {
            // do nothing
          });
      }
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  return (
    <LaunchDarklyContext.Provider
      value={{
        client,
        flags,
        initialized,
        currentAccountId,
        changeAccount,
      }}
    >
      {children}
    </LaunchDarklyContext.Provider>
  );
};

export const withLaunchDarkly:
  <T extends LaunchDarklyProps>(Component: ComponentType<T>) =>
ComponentType<Pick<T, Exclude<keyof T, keyof LaunchDarklyProps>>> = (Component: any) => (
  props => <Component {...props} {...useContext(LaunchDarklyContext)} />
);

export const useLaunchDarkly = () => useContext(LaunchDarklyContext);
